import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineBolt,
  HiOutlineTableCells,
  HiOutlineRocketLaunch,
  HiOutlineArrowRight,
  HiOutlineCheck,
  HiOutlineClipboardDocument,
  HiOutlineLockClosed,
  HiOutlineCommandLine,
  HiOutlineShieldCheck,
  HiOutlineInformationCircle,
  HiOutlineXMark,
} from 'react-icons/hi2';
import cloudApi from '../../services/cloudApi';
import analytics from '../../services/analytics';
import { useAuth } from '../../contexts/AuthContext';
import { copyText } from '../../utils/clipboard';
import CheckoutAuthStep from '../Checkout/CheckoutAuthStep';
import { CONNECT_CLIENTS } from './connectClients';
import ConnectMcpPanel from './ConnectMcpPanel';

// ============================================================================
// The setup wizard, as a reusable modal.
// ----------------------------------------------------------------------------
// A focused, one-CTA-per-step flow (Sign in → Connect) that drops in anywhere:
// the connect landing page opens it from its CTAs, and the Integrations "Claude"
// card opens it to connect. It is fully self-contained — it derives its own step
// from auth + plan and probes whether the OAuth authorization server is live —
// so a caller only has to toggle `isOpen`.
//
// Two kinds of final step, picked by `client` (see connectClients.jsx):
//   sign-in → Claude and ChatGPT. URL-only, no MCP jargon and no token: paste
//             one URL, sign in. Everything that differs between the two is
//             `client.setup`, so this file never names either of them. Claude
//             is the default, and what the Integrations Claude card gets.
//   token   → the any-agent story (Cursor, Claude Code and the rest): create an
//             access token, copy the snippet (ConnectMcpPanel).
// Only the final Connect step differs; sign-in, the plan gate and the shell are
// shared, so every story gets the same conversion path.
// ============================================================================

// What a paid user unlocks — used in the upsell. Terse label + one-line payoff,
// scannable at a glance. Claude-framed, no jargon.
const UPSELL_PERKS = [
  { icon: HiOutlineRocketLaunch, lead: 'Scrape anything', text: 'Any site, store, or list of URLs' },
  { icon: HiOutlineTableCells, lead: 'Ask in plain English', text: 'Query & clean your tables' },
  { icon: HiOutlineBolt, lead: 'Run automations', text: 'Straight from the chat' },
];

// The journey is just two steps. Needing a paid plan is a gate that surfaces
// inside the Connect step for free users — not a step of its own.
const WIZARD_STEPS = ['Sign in', 'Connect'];

// The wizard step, derived from auth + plan. Exported so the /connect-claude
// page can tag analytics with the exact same state the modal renders — the two
// can't drift. subscriptionLoading only matters once authenticated (it never
// resolves for signed-out visitors).
export const deriveConnectState = ({ isAuthenticated, subscriptionLoading, isPaidPlan }) => {
  if (!isAuthenticated) return 'signin';
  if (subscriptionLoading) return 'loading';
  if (isPaidPlan) return 'connect';
  return 'upsell';
};

// ============================================================================
// Small building blocks
// ============================================================================

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    copyText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      aria-label={copied ? 'Copied' : 'Copy'}
      className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
    >
      {copied
        ? <HiOutlineCheck className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
        : <HiOutlineClipboardDocument className="w-3.5 h-3.5" />}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
};

// A labelled value to copy. Copy rides the label row, and the pill below spans
// the field's full width — the whole point is that the value fits on one line
// (it still wraps rather than clips if it can't).
const CopyField = ({ label, text }) => (
  <div>
    <div className="flex items-center justify-between gap-3 mb-2.5">
      {label}
      <CopyButton text={text} />
    </div>
    <code className="block px-3 py-2.5 rounded-xl border border-gray-200 dark:border-white/[0.07] bg-white dark:bg-gray-900/60 text-xs font-mono text-gray-700 dark:text-gray-300 leading-relaxed break-words select-all">{text}</code>
  </div>
);

const StepNum = ({ children }) => (
  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 text-white text-[11px] font-bold flex items-center justify-center shadow-sm shadow-cyan-500/30">
    {children}
  </span>
);

const Stepper = ({ activeIndex }) => (
  <div className="flex items-center justify-center mb-5 sm:mb-7">
    {WIZARD_STEPS.map((label, i) => {
      const done = i < activeIndex;
      const active = i === activeIndex;
      return (
        <React.Fragment key={label}>
          <div className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-all duration-300 ${
                done
                  ? 'bg-emerald-500 text-white'
                  : active
                    ? 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white shadow-sm shadow-cyan-500/30 ring-2 ring-cyan-400/30'
                    : 'bg-gray-100 dark:bg-white/[0.06] text-gray-400 dark:text-gray-500'
              }`}
            >
              {done ? <HiOutlineCheck className="w-4 h-4" strokeWidth={2.5} /> : i + 1}
            </div>
            <span
              className={`text-[11px] sm:text-xs font-semibold transition-colors ${
                active
                  ? 'text-gray-900 dark:text-white'
                  : done
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              {label}
            </span>
          </div>
          {i < WIZARD_STEPS.length - 1 && (
            <div className={`w-5 sm:w-8 h-px mx-2 sm:mx-3 transition-colors duration-500 ${i < activeIndex ? 'bg-emerald-400/60' : 'bg-gray-200 dark:bg-white/10'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ============================================================================
// The state-aware panel — the one piece that changes by auth + plan.
// state: 'loading' | 'upsell' | 'connect' (signin is handled by the modal shell)
// ============================================================================

const ConnectPanel = ({ state, client, mcpUrl, oauthAvailable, onUpgrade, onPlanRequired, onSwitchStory }) => {
  if (state === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-14">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 dark:border-cyan-400" />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Checking your plan…</p>
      </div>
    );
  }

  if (state === 'upsell') {
    return (
      <div className="flex flex-col items-center text-center">
        {/* Hero focal point — the glowing PRO crest anchors the eye before the CTA. */}
        <div className="relative mb-5">
          <div className="absolute inset-0 -m-3 bg-gradient-to-br from-blue-500/25 via-cyan-500/25 to-purple-500/25 blur-2xl rounded-full" aria-hidden="true" />
          <div className="relative w-16 h-16 rounded-2xl p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-lg shadow-cyan-500/30">
            <div className="w-full h-full rounded-[14px] bg-white dark:bg-gray-900 flex items-center justify-center">
              <HiOutlineRocketLaunch className="w-8 h-8 text-cyan-500 dark:text-cyan-400" />
            </div>
          </div>
          <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white rounded-full bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 shadow-md shadow-cyan-500/30">
            PRO
          </span>
        </div>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight mb-2">
          Action requires PRO
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mb-6">
          You're signed in — <span className="font-semibold text-gray-700 dark:text-gray-200">upgrade to PRO</span> {client.upsellLine}.
        </p>

        {/* Compact perk list — dividers, not stacked cards, so it reads as one clean list. */}
        <div className="w-full max-w-xs mb-6 rounded-2xl border border-gray-100 dark:border-white/[0.06] divide-y divide-gray-100 dark:divide-white/[0.06] overflow-hidden bg-gray-50/60 dark:bg-white/[0.02]">
          {UPSELL_PERKS.map(({ icon: Icon, lead, text }) => (
            <div key={lead} className="flex items-center gap-3 px-4 py-2.5 text-left">
              <Icon className="w-5 h-5 flex-shrink-0 text-cyan-500 dark:text-cyan-400" />
              <span className="min-w-0 leading-tight">
                <span className="block text-sm font-semibold text-gray-800 dark:text-gray-100">{lead}</span>
                <span className="block text-xs text-gray-400 dark:text-gray-500">{text}</span>
              </span>
            </div>
          ))}
        </div>

        <button
          onClick={onUpgrade}
          className="w-full max-w-xs inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white rounded-xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-400 hover:via-cyan-400 hover:to-purple-400 shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
        >
          <HiOutlineRocketLaunch className="w-4 h-4" />
          Upgrade to PRO
        </button>
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          Cloud credits included · cancel anytime
        </p>
      </div>
    );
  }

  // state === 'connect' — paid user. The any-agent story hands over a token
  // instead of a URL, which is a flow of its own.
  if (!client.setup) {
    return (
      <ConnectMcpPanel
        mcpUrl={mcpUrl}
        oauthAvailable={oauthAvailable}
        onPlanRequired={onPlanRequired}
        onSwitchStory={onSwitchStory}
      />
    );
  }

  // The sign-in stories (Claude, ChatGPT): copy one URL, then follow that app's
  // steps. Everything app-specific comes off `client.setup`.
  const { PartnerTile, title, subtitle, steps, oauthNote, note, cli } = client.setup;
  return (
    <div>
      <div className="text-center mb-6">
        {/* Branded handshake — our logo linked to the app's mark, so "add us to
            <app>" reads at a glance. Mirrors the OAuth consent screen's pairing
            so the two connect surfaces feel like one product. */}
        <div className="relative flex items-center justify-center gap-3 mb-5">
          <div className="absolute w-40 h-16 bg-cyan-400/20 dark:bg-cyan-400/15 blur-2xl rounded-full" aria-hidden="true" />
          {/* Ours */}
          <div className="relative w-12 h-12 rounded-xl p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-lg shadow-cyan-500/20 shrink-0">
            <div className="w-full h-full bg-white rounded-[10px] p-1.5 flex items-center justify-center">
              <img src="/logo-128.png" alt="Ultimate Web Scraper logo" className="w-full h-full" />
            </div>
          </div>
          {/* Connector */}
          <span className="relative flex items-center" aria-hidden="true">
            <span className="w-3 h-px bg-gradient-to-r from-transparent to-cyan-400/60" />
            <HiOutlineArrowRight className="w-4 h-4 text-cyan-500/80 dark:text-cyan-400/70" />
            <span className="w-3 h-px bg-gradient-to-l from-transparent to-cyan-400/60" />
          </span>
          {/* Theirs — a finished app tile that fills its container edge-to-edge
              (no inner tile). A hairline inset ring gives it the same defined
              edge as our gradient-bordered tile, so the two read as a matched
              pair. Shown unmodified, per each owner's trademark guidelines. */}
          <PartnerTile />
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight mb-2">
          {title}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-md mx-auto">
          {subtitle}
        </p>
      </div>

      <div className="max-w-lg mx-auto space-y-3">
        {/* Step 1 is the same everywhere — the URL lives right here, and the
            app's own steps below can refer to it. */}
        <div className="p-3.5 rounded-2xl bg-gray-50/70 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.05]">
          <CopyField
            label={(
              <span className="flex items-center gap-3.5 min-w-0">
                <StepNum>1</StepNum>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Copy this URL</span>
              </span>
            )}
            text={mcpUrl}
          />
        </div>

        {steps.map((step, i) => (
          <div key={i} className="flex gap-3.5 p-3.5 rounded-2xl bg-gray-50/70 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.05]">
            <StepNum>{i + 2}</StepNum>
            <p className="flex-1 pt-0.5 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              {step}
            </p>
          </div>
        ))}

        {oauthAvailable && (
          <p className="flex items-start gap-1.5 pt-1 px-1 text-xs text-emerald-600 dark:text-emerald-400 leading-relaxed">
            <HiOutlineShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            {oauthNote}
          </p>
        )}

        {/* Prerequisites the app itself imposes (plan tier, platform) — better
            said before they start than discovered at step 3. */}
        {note && (
          <div className="flex gap-2.5 px-3.5 py-3 rounded-xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-200/60 dark:border-white/[0.06]">
            <HiOutlineInformationCircle className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{note}</p>
          </div>
        )}

        {/* Secondary: the CLI sibling gets the same URL as a one-liner. */}
        {cli && (
          <details className="group rounded-xl border border-gray-200/70 dark:border-white/[0.06] bg-gray-50/50 dark:bg-white/[0.02] overflow-hidden">
            <summary className="flex items-center gap-2 px-3.5 py-2.5 cursor-pointer text-xs font-medium text-gray-600 dark:text-gray-300 select-none list-none">
              <HiOutlineCommandLine className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              {cli.summary}
              <HiOutlineArrowRight className="w-3.5 h-3.5 ml-auto text-gray-400 transition-transform group-open:rotate-90" />
            </summary>
            <div className="px-3.5 pb-3.5 pt-1">
              <CopyField
                label={<p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{cli.intro}</p>}
                text={cli.command(mcpUrl)}
              />
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// The modal
// ============================================================================

const ConnectClaudeModal = ({ isOpen, onClose, source = 'modal', client = CONNECT_CLIENTS.claude, onSwitchStory }) => {
  const navigate = useNavigate();
  const { isAuthenticated, subscription, subscriptionLoading } = useAuth();

  // A 402 from token creation means the plan check we ran up front is stale
  // (plan changed in another tab, or the workspace was switched). Fall back to
  // the same upsell a known-free user sees rather than surfacing a raw error.
  const [planRequired, setPlanRequired] = useState(false);
  useEffect(() => { if (isOpen) setPlanRequired(false); }, [isOpen]);

  // Whether the OAuth authorization server is live (OAUTH_ENABLED on the
  // backend). When on, the connector URL alone authenticates via sign-in —
  // exactly the "just the connect URL" experience this flow sells.
  const [oauthAvailable, setOauthAvailable] = useState(false);
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    fetch(`${cloudApi.getApiUrl()}/.well-known/oauth-authorization-server`)
      .then((res) => { if (!cancelled) setOauthAvailable(res.ok); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Connecting requires a paid plan — the backend gates it with 402
  // PLAN_REQUIRED. Any non-free plan is "paid" for this flow's purposes.
  const planName = subscription?.subscription?.plan?.name || 'free';
  const isPaidPlan = planName !== 'free';
  const mcpUrl = cloudApi.getMcpServerUrl();

  const derived = deriveConnectState({ isAuthenticated, subscriptionLoading, isPaidPlan });
  const panelState = planRequired && derived === 'connect' ? 'upsell' : derived;
  // Stepper position: Sign in (0) → Connect (1). Once signed in, they're on the
  // Connect step; the paywall (for free plans) is that step's content.
  const activeIndex = isAuthenticated ? 1 : 0;

  const handleUpgrade = () => {
    analytics.capture(analytics.EVENTS.CONNECT_CLAUDE_UPGRADE_CLICKED, { plan: planName, source, client: client.key });
    navigate('/plans');
  };

  // Portal to <body>: callers embed this modal inside cards that use
  // `backdrop-blur` (a backdrop-filter), and any filtered/transformed ancestor
  // becomes the containing block for `position: fixed` — which would trap the
  // overlay inside that card instead of covering the viewport. Rendering into
  // <body> escapes every such ancestor, for every embedder.
  return createPortal(
    // The panel is centred with `my-auto`, NOT `items-center`: the steps grow
    // with the story (and the viewport shrinks in landscape), and centred flex
    // items that outgrow their scroll container overflow past the top edge,
    // where nothing can scroll to them. Auto margins collapse to 0 instead, so
    // a tall panel simply starts at the top and scrolls — close button, eyebrow
    // and stepper stay reachable at any height.
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-md flex items-start justify-center p-4 pt-6 overflow-y-auto overscroll-contain animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={client.modalEyebrow}
    >
      <div className={`relative w-full my-auto ${client.wideModal && panelState === 'connect' ? 'max-w-lg' : 'max-w-md'}`} onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute -top-2 right-0 sm:-right-2 z-10 p-2 rounded-xl bg-white/90 dark:bg-gray-800/90 backdrop-blur border border-gray-200/70 dark:border-white/[0.08] text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 shadow-sm hover:scale-105 active:scale-95 transition-all"
        >
          <HiOutlineXMark className="w-5 h-5" />
        </button>

        {/* The eyebrow + stepper give back their vertical space first when the
            viewport is short — the steps below are the part people need. */}
        <div className="text-center mb-4 sm:mb-6 pt-2">
          <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-cyan-600/80 dark:text-cyan-400/70 mb-1.5">{client.modalEyebrow}</p>
          <Stepper activeIndex={activeIndex} />
        </div>

        {/* Keyed by step so each transition eases in instead of blinking. Fade
            only — a transform here would sit over the card's backdrop-blur and
            make Chrome rasterize the text blurry on a GPU layer. */}
        <div key={panelState} className="animate-in fade-in duration-500 ease-out">
          {panelState === 'signin' ? (
            <CheckoutAuthStep subtitle={client.signinSubtitle} />
          ) : (
            <div className="relative bg-white/90 dark:bg-gray-900/70 backdrop-blur-xl border border-gray-200/70 dark:border-gray-700/50 rounded-2xl shadow-xl dark:shadow-2xl p-6 sm:p-8">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" aria-hidden="true" />
              <ConnectPanel
                state={panelState}
                client={client}
                mcpUrl={mcpUrl}
                oauthAvailable={oauthAvailable}
                onUpgrade={handleUpgrade}
                onPlanRequired={() => setPlanRequired(true)}
                onSwitchStory={onSwitchStory}
              />
            </div>
          )}
        </div>

        <p className="mt-5 flex items-center gap-1.5 justify-center text-xs text-gray-400 dark:text-gray-500">
          <HiOutlineLockClosed className="w-3.5 h-3.5" />
          {client.modalFooter}
        </p>
      </div>
    </div>,
    document.body
  );
};

export default ConnectClaudeModal;
